import NextLink from 'next/link'
import { ISbStoryData } from 'storyblok-js-client'

import { StoryblokImage } from 'components/blocks/StoryblokImage'
import { useTranslation } from 'lib/i18n'
import {
  BlogPostStoryblok,
  PatientStoryStoryblok,
  ServiceStoryblok,
} from 'lib/storyblok/types'
import { sortServicesTypesAlphabetically } from 'lib/utils/content'
import { formatDateLocale } from 'lib/utils/datetime'
import { removeTrailingSlash } from 'lib/utils/string'
import { cn } from 'lib/utils/tailwind'

import { TagList } from '../../../../ui/TagList'
import { getThumbCategories } from './BigThumb'

type Props = {
  item: ISbStoryData<BlogPostStoryblok> | ISbStoryData<PatientStoryStoryblok>
  variant?: 'cover' | 'default'
  maxVisibleTags?: number
  className?: string
}

export const ContentThumb = ({
  item,
  variant = 'default',
  maxVisibleTags = 2,
  className,
  ...props
}: Props): JSX.Element => {
  const { locale, i18n } = useTranslation()

  const categories = Array.from(
    new Set(
      item.content.component === 'patient-story'
        ? getThumbCategories(item.content.categories)
        : getThumbCategories(item.content.services)
    )
  ) as Array<ServiceStoryblok['type']>

  return (
    <NextLink
      href={removeTrailingSlash(`/${item.full_slug}`)}
      {...props}
      className={cn(
        'text-foreground flex flex-col gap-5',
        variant === 'cover' &&
          `md:flex-row md:justify-between p-3 md:p-4 bg-background-secondary-${categories[0]}-light rounded-5 md:rounded-4`,
        className
      )}
    >
      {variant === 'cover' &&
        item.content.cover_image &&
        item.content.cover_image.filename && (
          <div className="md:hidden flex overflow-hidden w-full h-[240px] rounded-4">
            <StoryblokImage
              asset={item.content.cover_image}
              className="object-cover"
            />
          </div>
        )}

      {variant === 'default' &&
        item.content.cover_image &&
        item.content.cover_image.filename && (
          <div className="flex overflow-hidden h-[252px] md:h-[310px] md:w-full rounded-4">
            <StoryblokImage
              asset={item.content.cover_image}
              className="object-cover"
            />
          </div>
        )}

      <div
        className={cn(
          'flex flex-col gap-3',
          variant === 'cover' && 'md:justify-between'
        )}
      >
        <div className="flex items-center gap-3">
          {categories?.length > 0 && (
            <TagList
              hasHiddenTags
              hasColorVariants
              maxVisibleTags={maxVisibleTags}
              tags={sortServicesTypesAlphabetically(categories)}
            />
          )}

          <div className="text-sixteen flex items-center gap-1">
            {item.content.author && (
              <span>{`${i18n('by')} ${item.content.author}`}</span>
            )}
            {item.content.author && item.first_published_at && <span>⋅</span>}
            {item.first_published_at && (
              <span>{formatDateLocale(item.first_published_at, locale)}</span>
            )}
          </div>
        </div>

        <h2 className="text-title-small font-regular line-clamp-2">
          {item.content.title}
        </h2>
      </div>

      {variant === 'cover' &&
        item.content.cover_image &&
        item.content.cover_image.filename && (
          <div className="hidden md:flex overflow-hidden h-[235px] w-[284px] rounded-3">
            <StoryblokImage
              asset={item.content.cover_image}
              className="object-cover"
            />
          </div>
        )}
    </NextLink>
  )
}
